/**
 * letway.ts —— 让位规则中枢（二期 FR-E2 / FR-E3，设计方案 §4「让位而非压制」）
 *
 * 背景：能「跨上下文盖穿」的元素（toast/pwa/tooltip/modal）都是 body 根兄弟，而被它们
 * 遮住的关键浮层（单机结算 trap 在 overlay-root 80、多人浮层 trap 在 mp-root 0）都封在低值 SC 里。
 * 本模块用「让位」根治：当**关键/打断类浮层在场**时，低优先级元素主动退让，而不是靠更高的 z 去压。
 *
 * 单一事实源：所有「浮层在场」状态经 setLetwayFloat 登记，recompute 统一推导三件事——
 *   ① toast 静默排队   → setToastHold（复用 state/toasts.ts 的静默队列，AC-E2.1 / AC-B.3）
 *   ② pwa 让位         → letwayStore.pwaYield（PwaHints 消费：安装条隐藏 / 断网 chip 下移，AC-E2.2/E2.3/E3.1）
 *   ③ 禁触发辅助浮层    → body[data-aux-block]（CSS 禁 tooltip + ConfirmPopover 拦截，AC-E3.3 方案二）
 *
 * 单机屏（.gs*）与多人屏（.mp-root）永不同屏（App 二选一渲染），因此两屏浮层不会同时登记。
 */

import { createStore, type Store } from './store';
import { setToastHold } from './toasts';

/** 会触发让位的浮层种类（单机 + 多人）。 */
export type FloatKind =
  | 'settle' // 单机结算/摊牌浮层在场（含淡出期）
  | 'alert' // 单机关键系统 alert（预留）
  | 'modal' // 单机全屏模态（淘汰 / 设置·历史抽屉）
  | 'mp-confirm' // 多人二次确认（打断档，portal 到 body 根）
  | 'mp-sync' // 多人同步遮罩（阻断态）
  | 'mp-drawer' // 多人历史/设置抽屉（查阅面板）
  | 'mp-review'; // 多人离开/复盘抽屉（查阅面板）

/**
 * 关键浮层在场 → toast 静默排队（设计 §4.1）。
 *
 * 第 23 轮（QA BUG-E-01）：drawer/review 亦纳入静默。
 *   drawer(55)/review(45) 封在 `.mp-root`(z:auto=0) 内部，对 body 根有效层级仅 0，
 *   而 toast 是 body 根兄弟（115），必然绘制在抽屉/复盘**面板体**之上（PRD 7.X「drawer/review × toast ⑥ 覆盖数 = 0」判据未达标）。
 *   查阅面板在场时让 toast 静默排队、关闭后补显最新有效项，从源头消除面板体被瞬时 toast 覆盖。
 */
const TOAST_HOLD: ReadonlySet<FloatKind> = new Set<FloatKind>([
  'settle',
  'alert',
  'modal',
  'mp-confirm',
  'mp-sync',
  'mp-drawer',
  'mp-review',
]);

/** pwa 让位（安装条隐藏 / 断网 chip 下移）：任一 mp 全屏浮层 + 单机对局核心态（设计 §4.2，范围比 toast 更宽）。 */
const PWA_YIELD: ReadonlySet<FloatKind> = new Set<FloatKind>([
  'settle',
  'alert',
  'modal',
  'mp-confirm',
  'mp-sync',
  'mp-drawer',
  'mp-review',
]);

/** 打断类浮层在场 → 禁触发 tooltip / popover（设计 §4.3，P3 单屏互斥）。 */
const BLOCK_AUX: ReadonlySet<FloatKind> = new Set<FloatKind>(['settle', 'alert', 'modal', 'mp-confirm']);

export interface LetwayState {
  /** true 时 PwaHints 应隐藏安装条、下移/缩小断网 chip 到不与关键浮层相交的安全区。 */
  readonly pwaYield: boolean;
  /** true 时打断类浮层在场：禁触发 tooltip / popover。 */
  readonly blockAux: boolean;
}

/** 当前在场的浮层集合（按种类去重；单机各态与 mp 各态互斥，无需引用计数）。 */
const active = new Set<FloatKind>();

export const letwayStore: Store<LetwayState> = createStore<LetwayState>({ pwaYield: false, blockAux: false });

function hasAny(set: ReadonlySet<FloatKind>): boolean {
  for (const k of active) if (set.has(k)) return true;
  return false;
}

function recompute(): void {
  const toastHold = hasAny(TOAST_HOLD);
  const pwaYield = hasAny(PWA_YIELD);
  const blockAux = hasAny(BLOCK_AUX);

  // ① toast 静默排队（复用既有队列；挂载即静默、卸载出队，AC-E2.1 过渡守护）
  setToastHold(toastHold);

  // ③ 禁触发辅助浮层：以 body 属性驱动 CSS（tooltip）与组件拦截（ConfirmPopover）
  if (typeof document !== 'undefined') {
    if (blockAux) document.body.setAttribute('data-aux-block', '');
    else document.body.removeAttribute('data-aux-block');
  }

  // ② pwa 让位：发布给 PwaHints 消费
  const prev = letwayStore.get();
  if (prev.pwaYield !== pwaYield || prev.blockAux !== blockAux) {
    letwayStore.set({ pwaYield, blockAux });
  }
}

/**
 * 登记/注销某个浮层在场态。典型用法（React effect）：
 *   useEffect(() => { setLetwayFloat('mp-confirm', open); return () => setLetwayFloat('mp-confirm', false); }, [open]);
 * 幂等：重复设置同一状态不重复计算，StrictMode 双挂载安全。
 */
export function setLetwayFloat(kind: FloatKind, on: boolean): void {
  const had = active.has(kind);
  if (on === had) return;
  if (on) active.add(kind);
  else active.delete(kind);
  recompute();
}

/** 同步读取「是否禁触发辅助浮层」，供 ConfirmPopover 等在渲染期拦截触发（AC-E3.3）。 */
export function isAuxBlocked(): boolean {
  return letwayStore.get().blockAux;
}

/** 测试/复位辅助：清空全部在场态（不影响其它模块的直接调用）。 */
export function resetLetway(): void {
  if (active.size === 0) return;
  active.clear();
  recompute();
}
